import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Loader2, Zap, RefreshCw, Shield, KeyRound, CheckCircle2, Terminal, Trash2, Pencil } from "lucide-react";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { formatDate } from "@/lib/format-date";
import { useAuthStore } from "@/stores/auth-store";
import { hasPermission } from "@/lib/permissions";
import { useTestConnection, useTriggerSync, useConfigureConnection, useIntegrationBranding } from "@/hooks/use-integrations";
import { useSyncConsole } from "@/hooks/use-sync-console";
import { ConfigureConnectionDialog } from "@/components/integrations/ConfigureConnectionDialog";
import type { EdcConnection } from "@/api/types";

const CREDENTIAL_FIELDS: Record<string, Array<{ name: string; label: string; type: string }>> = {
  veeva: [
    { name: "username", label: "Username", type: "text" },
    { name: "password", label: "Password", type: "password" },
  ],
  medrio: [
    { name: "username", label: "Username", type: "text" },
    { name: "password", label: "Password", type: "password" },
    { name: "customerApiKey", label: "Customer API Key", type: "password" },
    { name: "studyApiKey", label: "Study API Key (Export)", type: "password" },
  ],
  redcap: [
    { name: "apiToken", label: "API Token", type: "password" },
  ],
  redcap_cloud: [
    { name: "apiToken", label: "API Token", type: "password" },
  ],
  imednet: [
    { name: "apiKey", label: "API Key", type: "password" },
    { name: "securityKey", label: "Security Key", type: "password" },
  ],
  medidata: [
    { name: "clientId", label: "Client ID", type: "text" },
    { name: "clientSecret", label: "Client Secret", type: "password" },
  ],
  castor: [
    { name: "clientId", label: "Client ID", type: "text" },
    { name: "clientSecret", label: "Client Secret", type: "password" },
  ],
};

export function ConnectionInspector({ data }: { data: unknown }) {
  const conn = data as EdcConnection;
  const { role } = useAuthStore();
  const canManage = role ? hasPermission(role, "connections:manage") : false;
  const testMutation = useTestConnection();
  const syncMutation = useTriggerSync();
  const configureMutation = useConfigureConnection();

  const { data: brandingData } = useIntegrationBranding();
  const integrationBranding = (brandingData?.integrations ?? []).find((i) => i.id === conn.system);
  const [showEditDialog, setShowEditDialog] = useState(false);

  const [showCredentials, setShowCredentials] = useState(false);
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "success" | "error">("idle");
  const [saveError, setSaveError] = useState("");
  const [showConsole, setShowConsole] = useState(false);
  const { entries: consoleEntries } = useSyncConsole(showConsole ? conn.id : null);
  const consoleEndRef = useRef<HTMLDivElement>(null);

  const credFields = CREDENTIAL_FIELDS[conn.system] ?? [];

  // Auto-scroll console to bottom
  useEffect(() => {
    consoleEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [consoleEntries]);

  function setCredField(name: string, value: string) {
    setCredentials((prev) => ({ ...prev, [name]: value }));
  }

  async function handleSaveCredentials() {
    const filled = Object.entries(credentials).filter(([, v]) => v.trim());
    if (filled.length === 0) return;

    setSaveStatus("saving");
    setSaveError("");
    try {
      await configureMutation.mutateAsync({
        system: conn.system,
        label: conn.label,
        baseUrl: conn.baseUrl,
        credentials: Object.fromEntries(filled),
        connectionId: conn.id,
        syncScope: conn.syncScope,
        syncSchedule: conn.syncSchedule,
      });
      setSaveStatus("success");
      setCredentials({});
      setTimeout(() => setSaveStatus("idle"), 3000);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Failed to save credentials");
      setSaveStatus("error");
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground">{conn.label}</h3>
        <p className="mt-0.5 text-sm text-muted-foreground">{conn.system}</p>
        <div className="mt-2">
          <StatusBadge status={conn.status} />
        </div>
      </div>

      <div className="space-y-2">
        <Detail label="Base URL" value={conn.baseUrl || "—"} />
        <Detail label="Schedule" value={conn.syncSchedule || "—"} />
        <Detail label="Scopes" value={conn.syncScope?.join(", ") || "—"} />
      </div>

      {/* Active APIs / Capabilities */}
      {conn.system === "medrio" && (conn.capabilities ?? []).length > 0 && (
        <div>
          <h4 className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Active APIs
          </h4>
          <div className="space-y-1.5">
            {(conn.capabilities ?? []).includes("connect_api") && (
              <div className="flex items-center gap-2 rounded-md bg-emerald-500/10 px-3 py-2">
                <div className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                <span className="text-xs font-medium text-emerald-500">Connect API</span>
                <span className="ml-auto text-[10px] text-muted-foreground">Studies, Sites, Subjects</span>
              </div>
            )}
            {(conn.capabilities ?? []).includes("export_api") && (
              <div className="flex items-center gap-2 rounded-md bg-blue-500/10 px-3 py-2">
                <div className="h-1.5 w-1.5 rounded-full bg-blue-500" />
                <span className="text-xs font-medium text-blue-500">Export API (mSource)</span>
                <span className="ml-auto text-[10px] text-muted-foreground">ODM Export, Study Design + Clinical Data</span>
              </div>
            )}
            {(conn.capabilities ?? []).includes("data_views_api") && (
              <div className="flex items-center gap-2 rounded-md bg-violet-500/10 px-3 py-2">
                <div className="h-1.5 w-1.5 rounded-full bg-violet-500" />
                <span className="text-xs font-medium text-violet-500">Data Views API</span>
                <span className="ml-auto text-[10px] text-muted-foreground">Clinical Data Reports, Audit Trail</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {canManage && (
        <div className="flex gap-2">
          <button
            onClick={() => testMutation.mutate({ connectionId: conn.id })}
            disabled={testMutation.isPending}
            className="flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-xs font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-50"
          >
            {testMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3" />}
            Re-test
          </button>
          <button
            onClick={() => syncMutation.mutate({ connectionId: conn.id })}
            disabled={syncMutation.isPending}
            className="flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-xs font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-50"
          >
            {syncMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            Sync Now
          </button>
          {integrationBranding && (
            <button
              onClick={() => setShowEditDialog(true)}
              className="flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-xs font-medium text-foreground transition-colors hover:bg-muted"
            >
              <Pencil className="h-3 w-3" />
              Edit Connection
            </button>
          )}
        </div>
      )}

      {/* Credentials Section */}
      {canManage && credFields.length > 0 && (
        <div>
          <button
            onClick={() => setShowCredentials(!showCredentials)}
            className="flex w-full items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
          >
            <KeyRound className="h-3.5 w-3.5" />
            {showCredentials ? "Hide Credentials" : "Update Credentials"}
          </button>

          {showCredentials && (
            <div className="mt-3 space-y-3">
              {credFields.map((field) => (
                <div key={field.name} className="space-y-1">
                  <label className="text-xs text-muted-foreground">{field.label}</label>
                  <input
                    type={field.type}
                    value={credentials[field.name] ?? ""}
                    onChange={(e) => setCredField(field.name, e.target.value)}
                    placeholder={`Enter ${field.label.toLowerCase()}`}
                    disabled={saveStatus === "saving"}
                    className="flex h-8 w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                  />
                </div>
              ))}

              {saveStatus === "error" && saveError && (
                <p className="text-xs text-destructive">{saveError}</p>
              )}

              {saveStatus === "success" && (
                <p className="flex items-center gap-1 text-xs text-emerald-500">
                  <CheckCircle2 className="h-3 w-3" /> Credentials updated
                </p>
              )}

              <div className="flex gap-2">
                <button
                  onClick={handleSaveCredentials}
                  disabled={saveStatus === "saving" || Object.values(credentials).every((v) => !v.trim())}
                  className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  {saveStatus === "saving" ? (
                    <><Loader2 className="h-3 w-3 animate-spin" /> Saving...</>
                  ) : (
                    "Save & Re-test"
                  )}
                </button>
                <button
                  onClick={() => { setShowCredentials(false); setCredentials({}); setSaveStatus("idle"); }}
                  className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
                >
                  Cancel
                </button>
              </div>

              <div className="flex items-center gap-2 rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
                <Shield className="h-3.5 w-3.5 shrink-0" />
                Credentials are encrypted in Secret Manager. Previous values cannot be viewed.
              </div>
            </div>
          )}
        </div>
      )}

      {/* Credential Security Info (shown when credentials section is collapsed) */}
      {(!canManage || !showCredentials) && (
        <div className="flex items-center gap-2 rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
          <Shield className="h-3.5 w-3.5 shrink-0" />
          Credentials stored in Secret Manager
        </div>
      )}

      {conn.syncState && (
        <div>
          <h4 className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Sync Status
          </h4>
          <div className="rounded-lg border border-border divide-y divide-border">
            <SyncDetail
              label="Last Sync"
              value={conn.syncState.lastSuccessfulSync
                ? formatDate(conn.syncState.lastSuccessfulSync)
                : "Never"}
            />
            <SyncDetail label="In Progress" value={conn.syncState.inProgress ? "Yes" : "No"} />
            {conn.syncState.lastSyncStats && (
              <>
                <SyncDetail
                  label="Studies Synced"
                  value={String(conn.syncState.lastSyncStats.studiesUpserted ?? 0)}
                />
                <SyncDetail
                  label="Sites Synced"
                  value={String(conn.syncState.lastSyncStats.sitesUpserted ?? 0)}
                />
                <SyncDetail
                  label="Records Synced"
                  value={String(conn.syncState.lastSyncStats.edcRecordsUpserted ?? 0)}
                />
                {conn.syncState.lastSyncStats.errors > 0 && (
                  <SyncDetail
                    label="Errors"
                    value={String(conn.syncState.lastSyncStats.errors)}
                  />
                )}
              </>
            )}
          </div>

          {conn.syncState.lastError && (
            <div className="mt-3 rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {conn.syncState.lastError.message}
            </div>
          )}
        </div>
      )}

      {/* Sync Console */}
      <div>
        <button
          onClick={() => setShowConsole(!showConsole)}
          className="flex w-full items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
        >
          <Terminal className="h-3.5 w-3.5" />
          {showConsole ? "Hide Console" : "Show Console"}
        </button>

        {showConsole && (
          <div className="mt-3">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] text-muted-foreground">
                {consoleEntries.length} entries
              </span>
              {consoleEntries.length > 0 && (
                <button
                  onClick={() => setShowConsole(false)}
                  className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Trash2 className="h-2.5 w-2.5" />
                  Close
                </button>
              )}
            </div>
            <div className="h-64 overflow-auto rounded-md bg-zinc-950 border border-zinc-800 p-2 font-mono text-[11px] leading-relaxed">
              {consoleEntries.length === 0 && (
                <div className="flex h-full items-center justify-center text-zinc-600">
                  Waiting for sync activity…
                </div>
              )}
              {consoleEntries.map((entry) => {
                const time = entry.timestamp
                  ? new Date(entry.timestamp.seconds * 1000).toLocaleTimeString()
                  : "";
                const levelColor =
                  entry.level === "error" ? "text-red-400" :
                  entry.level === "warn" ? "text-yellow-400" :
                  "text-zinc-400";
                return (
                  <div key={entry.id} className="flex gap-1.5 py-px">
                    <span className="shrink-0 text-zinc-600">{time}</span>
                    <span className={`shrink-0 ${levelColor}`}>
                      [{entry.level.toUpperCase().padEnd(5)}]
                    </span>
                    <span className="text-zinc-300">{entry.message}</span>
                  </div>
                );
              })}
              <div ref={consoleEndRef} />
            </div>
          </div>
        )}
      </div>

      {/* Edit Connection Dialog */}
      {showEditDialog && integrationBranding && createPortal(
        <ConfigureConnectionDialog
          integration={integrationBranding}
          connection={conn}
          onClose={() => setShowEditDialog(false)}
        />,
        document.body,
      )}
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <p className="text-sm text-foreground break-all">{value}</p>
    </div>
  );
}

function SyncDetail({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 px-3 py-2">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-xs font-medium text-foreground">{value}</span>
    </div>
  );
}
